import { useState, useEffect } from 'react';
import { useAuth } from './use-auth';
import { supabaseHelpers } from '@/lib/supabase';

export function useUserName() {
  const { user } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserName = async () => {
      try {
        // Try to get the user's name from localStorage first (for immediate display)
        const savedName = localStorage.getItem('vyzle-chat-user-name');
        if (savedName) {
          setUserName(savedName);
        }

        // If user is authenticated, try to get from Supabase
        if (user?.uid) {
          try {
            const userData = await supabaseHelpers.getUser(user.uid);
            if (userData?.display_name) {
              setUserName(userData.display_name);
              // Update localStorage to keep it in sync
              localStorage.setItem('vyzle-chat-user-name', userData.display_name);
            } else if (savedName) {
              // If we have a name in localStorage but not in Supabase, save it
              await supabaseHelpers.updateUser(user.uid, { display_name: savedName });
            } else {
              // Fallback to generated name
              const generatedName = `Vyzle_${user.uid.slice(-4).toUpperCase()}`;
              setUserName(generatedName);
              await supabaseHelpers.updateUser(user.uid, { display_name: generatedName });
              localStorage.setItem('vyzle-chat-user-name', generatedName);
            }
          } catch (error) {
            console.warn('Failed to load user name from Supabase:', error);
            // Fallback to localStorage or generated name
            if (!savedName) {
              const generatedName = `Vyzle_${user.uid.slice(-4).toUpperCase()}`;
              setUserName(generatedName);
              localStorage.setItem('vyzle-chat-user-name', generatedName);
            }
          }
        } else {
          // For non-authenticated users, use localStorage or generate a temporary name
          if (!savedName) {
            const generatedName = `Vyzle_${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
            setUserName(generatedName);
            localStorage.setItem('vyzle-chat-user-name', generatedName);
          }
        }
      } catch (error) {
        console.error('Error loading user name:', error);
        // Fallback to generated name
        const generatedName = `Vyzle_${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        setUserName(generatedName);
        localStorage.setItem('vyzle-chat-user-name', generatedName);
      } finally {
        setLoading(false);
      }
    };

    loadUserName();
  }, [user]);

  const updateUserName = async (name: string) => {
    try {
      setUserName(name);
      // Update localStorage immediately for responsive UI
      localStorage.setItem('vyzle-chat-user-name', name);

      // Update Supabase if user is authenticated
      if (user?.uid) {
        await supabaseHelpers.updateUser(user.uid, { display_name: name });
      }
    } catch (error) {
      console.error('Failed to update user name:', error);
      // Revert localStorage if Supabase update failed
      const savedName = localStorage.getItem('vyzle-chat-user-name');
      if (savedName && savedName !== name) {
        setUserName(savedName);
      }
      throw error;
    }
  };

  return { userName, updateUserName, loading };
} 